import { useQuery } from '@tanstack/react-query';
import { ChevronRight, FolderClosed, FolderOpen, Inbox, UserCheck, UserX } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';

import { QueryKey } from '@/Config/api/queryKey';
import { InboxTypeEnum, IndexPath } from '@/Config/app/router/index.path';
import { useCheckContainsIndexPath } from '@/Config/app/router/useIndexPath.hook';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import {
  AnimatedCollapsibleContent,
  Collapsible,
  CollapsibleTrigger,
} from '@/Domain/components/Shared/Collapsible/Collapsible';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/Domain/components/Shared/Sidebar/Sidebar';
import { InboxStatus, UserInboxStatus } from '@/Domain/components/SocketProvider';
import {
  MessageCircleMoreIcon,
  MessageCircleMoreIconHandle,
} from '@/Domain/components/icons/MessageCircleMoreIcon';
import { cn } from '@/Domain/utils/cn';

const formatBadgeCount = (count: number | undefined) => {
  if (count === undefined || count === 0) return undefined;
  if (count >= 1000000) {
    return (count / 1000000).toFixed(1).replace(/\.0$/, '') + 'm';
  }
  if (count >= 100000) {
    return (count / 1000).toFixed(0) + 'k';
  }
  return count.toLocaleString();
};

export enum ConversationTypeEnum {
  All = 'all',
  Open = 'open',
  OpenAssigned = 'openAssigned',
  OpenUnassigned = 'openUnassigned',
  Resolved = 'resolved',
}

const conversationToInboxTypes: Record<ConversationTypeEnum, readonly InboxTypeEnum[]> = {
  [ConversationTypeEnum.Open]: [
    InboxTypeEnum.OpenAll,
    InboxTypeEnum.OpenAllAssigned,
    InboxTypeEnum.OpenAllUnassigned,
    InboxTypeEnum.OpenByMe,
    InboxTypeEnum.OpenByOther,
    InboxTypeEnum.OpenByAI,
    InboxTypeEnum.OpenUnassigned,
  ],
  [ConversationTypeEnum.OpenAssigned]: [
    InboxTypeEnum.OpenAllAssigned,
    InboxTypeEnum.OpenByAI,
    InboxTypeEnum.OpenByMe,
    InboxTypeEnum.OpenByOther,
  ],
  [ConversationTypeEnum.OpenUnassigned]: [InboxTypeEnum.OpenUnassigned],
  [ConversationTypeEnum.Resolved]: [
    InboxTypeEnum.ResolvedAll,
    InboxTypeEnum.ResolvedByHuman,
    InboxTypeEnum.AssumedResolvedByAI,
  ],
  [ConversationTypeEnum.All]: [],
};

export function isConversationTypeActive(
  conversationType: ConversationTypeEnum,
  inboxType: InboxTypeEnum | null,
): boolean {
  if (!inboxType) return false;
  return conversationToInboxTypes[conversationType].includes(inboxType);
}

export const SidebarInboxButton = ({ isLocked }: { isLocked: boolean }) => {
  const { t } = useTranslation('sidebar_menu');
  const navigate = useNavigate();
  const { state: sidebarState } = useSidebar();
  const iconRef = useRef<MessageCircleMoreIconHandle>(null);

  const isConversationActive = useCheckContainsIndexPath(IndexPath.inbox.root);
  const [isInboxOpen, setIsInboxOpen] = useState(isConversationActive);

  const [searchParams] = useSearchParams();
  const inboxType = searchParams.get('type') as InboxTypeEnum | null;

  const [isOpenConversationsOpen, setIsOpenConversationsOpen] = useState(
    isConversationTypeActive(ConversationTypeEnum.Open, inboxType),
  );

  useEffect(() => {
    setIsInboxOpen(isConversationActive);
  }, [isConversationActive]);
  useEffect(() => {
    setIsOpenConversationsOpen(isOpenConversationsOpen);
  }, [isOpenConversationsOpen]);

  const { data: inboxStatusData } = useQuery<InboxStatus>({
    queryKey: [QueryKey.inboxStatus.general],
  });
  const { data: userInboxStatusData } = useQuery<UserInboxStatus>({
    queryKey: [QueryKey.inboxStatus.my],
  });

  const openConversationsCount =
    inboxStatusData && userInboxStatusData
      ? inboxStatusData.open_conversations_counts.ai_assistant_conversations_count +
        inboxStatusData.open_conversations_counts.unassigned_conversations_count +
        userInboxStatusData.my_conversations_count +
        userInboxStatusData.other_conversations_count
      : 0;
  const openUnassignedConversationsCount = inboxStatusData
    ? inboxStatusData.open_conversations_counts.unassigned_conversations_count
    : 0;
  const openAssignedConversationsCount =
    userInboxStatusData && inboxStatusData
      ? userInboxStatusData.my_conversations_count +
        userInboxStatusData.other_conversations_count +
        inboxStatusData.open_conversations_counts.ai_assistant_conversations_count
      : 0;

  const resolvedConversationsCount = inboxStatusData
    ? inboxStatusData.resolved_conversations_counts.ai_assistant_conversations_count +
      inboxStatusData.resolved_conversations_counts.human_conversations_count
    : 0;

  return (
    <Collapsible
      asChild
      open={!isLocked && isInboxOpen}
      onOpenChange={!isLocked ? setIsInboxOpen : undefined}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild disabled={isLocked}>
          <SidebarMenuButton
            isActive={sidebarState === 'collapsed' && isConversationActive}
            tooltip={t('menu.conversations.inbox')}
            data-shortcut="2"
            onClick={
              !isLocked
                ? () => {
                    setIsInboxOpen(true);
                    navigate(IndexPath.inbox.root, { viewTransition: true });
                  }
                : undefined
            }
            className={cn(isLocked && 'opacity-50 pointer-events-none')}
            onMouseEnter={() => !isLocked && iconRef.current?.startAnimation()}
            onMouseLeave={() => !isLocked && iconRef.current?.stopAnimation()}
          >
            <MessageCircleMoreIcon ref={iconRef} size={16} />
            <span>{t('menu.conversations.inbox')}</span>
            <ChevronRight className="ml-auto group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <AnimatedCollapsibleContent open={!isLocked && isInboxOpen}>
          <SidebarMenuSub>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton asChild isActive={inboxType == undefined}>
                <NavLink to={IndexPath.inbox.root} viewTransition>
                  <Inbox />
                  <span>{t('menu.conversations.all')}</span>
                  {inboxStatusData?.total_conversations_count !== undefined &&
                    inboxStatusData?.total_conversations_count > 0 && (
                      <Badge variant="secondary" size="sm" className="ml-auto">
                        {formatBadgeCount(inboxStatusData.total_conversations_count)}
                      </Badge>
                    )}
                </NavLink>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton asChild isActive={inboxType == InboxTypeEnum.OpenAll}>
                <NavLink
                  to={{
                    pathname: IndexPath.inbox.root,
                    search: IndexPath.inbox.openAll,
                  }}
                  viewTransition
                >
                  <FolderOpen />
                  <span>{t('menu.conversations.open')}</span>
                  {openConversationsCount > 0 && (
                    <Badge variant="secondary" size="sm" className="ml-auto">
                      {formatBadgeCount(openConversationsCount)}
                    </Badge>
                  )}
                </NavLink>
              </SidebarMenuSubButton>
              <SidebarMenuSub className="py-0">
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    isActive={isConversationTypeActive(
                      ConversationTypeEnum.OpenAssigned,
                      inboxType,
                    )}
                    size="sm"
                    className="h-6 text-xs [&>svg]:size-3.5"
                  >
                    <NavLink
                      to={{
                        pathname: IndexPath.inbox.root,
                        search: IndexPath.inbox.openAllAssigned,
                      }}
                      viewTransition
                      onClick={(e) => {
                        if (
                          isConversationTypeActive(ConversationTypeEnum.OpenAssigned, inboxType)
                        ) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                    >
                      <UserCheck />
                      <span>{t('menu.conversations.assigned')}</span>
                      {openAssignedConversationsCount > 0 && (
                        <Badge variant="secondary" size="sm" className="ml-auto">
                          {formatBadgeCount(openAssignedConversationsCount)}
                        </Badge>
                      )}
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    isActive={isConversationTypeActive(
                      ConversationTypeEnum.OpenUnassigned,
                      inboxType,
                    )}
                    size="sm"
                    className="h-6 text-xs [&>svg]:size-3.5"
                  >
                    <NavLink
                      to={{
                        pathname: IndexPath.inbox.root,
                        search: IndexPath.inbox.openUnassigned,
                      }}
                      viewTransition
                    >
                      <UserX />
                      <span>{t('menu.conversations.unassigned')}</span>
                      {openUnassignedConversationsCount > 0 && (
                        <Badge variant="error" size="sm" className="ml-auto">
                          {formatBadgeCount(openUnassignedConversationsCount)}
                        </Badge>
                      )}
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </SidebarMenuSub>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                asChild
                isActive={isConversationTypeActive(ConversationTypeEnum.Resolved, inboxType)}
              >
                <NavLink
                  to={{
                    pathname: IndexPath.inbox.root,
                    search: IndexPath.inbox.assumedResolvedByAI,
                  }}
                  viewTransition
                  onClick={(e) => {
                    if (isConversationTypeActive(ConversationTypeEnum.Resolved, inboxType)) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <FolderClosed />
                  <span>{t('menu.conversations.resolved')}</span>
                  {resolvedConversationsCount > 0 && (
                    <Badge variant="secondary" size="sm" className="ml-auto">
                      {formatBadgeCount(resolvedConversationsCount)}
                    </Badge>
                  )}
                </NavLink>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          </SidebarMenuSub>
        </AnimatedCollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};
